import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import heroBackground from "@/assets/hero-background.jpg";

/**
 * Dynamic homepage hero that reveals the platform's internal mechanism
 * (buyer-lender matching, non-conventional financing, closing experts,
 * consultant value) through a rotating set of "punch phrases" targeted
 * at each audience. Keeps the six role CTAs and the AcquizGroup palette.
 *
 * Side cards stagger in row-by-row on mount: the first card of each side
 * slides in together, then the second pair, then the third pair. A full-width
 * "All Users" banner slides in left-to-right just above the progress dots.
 */

type Phrase = {
  id: number;
  text: { en: string; fr: string };
  audience: { en: string; fr: string };
  /** which side of the mechanism this phrase belongs to */
  side: "buyers" | "partners";
};

const PHRASES: Phrase[] = [
  // Left column (demand side) --------------------------------------------
  {
    id: 1,
    text: {
      en: "Matching Business and Lender",
      fr: "Matching Business and Lender",
    },
    audience: { en: "Seller + Lender", fr: "Vendeur + Prêteur" },
    side: "buyers",
  },
  {
    id: 2,
    text: {
      en: "No more financing race after LOI — choose the loan that fits you",
      fr: "Fini la course au financement après LOI — Choose the loan that fits you",
    },
    audience: { en: "Buyers", fr: "Acheteurs" },
    side: "buyers",
  },
  {
    id: 3,
    text: {
      en: "Find the closing experts you need",
      fr: "Trouvez les experts du closing dont vous avez besoin",
    },
    audience: {
      en: "Buyers · Sellers · Brokers",
      fr: "Acheteurs · Vendeurs · Courtiers",
    },
    side: "buyers",
  },
  // Right column (supply side) -------------------------------------------
  {
    id: 4,
    text: {
      en: "Quality clients without prospecting",
      fr: "Des clients de qualité sans prospection",
    },
    audience: { en: "Lenders", fr: "Prêteurs" },
    side: "partners",
  },
  {
    id: 5,
    text: {
      en: "Your know-how and license are worth gold",
      fr: "Votre savoir-faire et licence valent de l'or",
    },
    audience: { en: "Consultants", fr: "Consultants" },
    side: "partners",
  },
  {
    id: 6,
    text: {
      en: "Spending days filling out paperwork is ancient history",
      fr: "Perdre des jours à remplir de la paperasse, c'est de l'histoire ancienne",
    },
    audience: { en: "Brokers", fr: "Courtiers" },
    side: "partners",
  },
];

/** Full-width banner card shown just above the progress dots. */
const ALL_USERS = {
  prefix: {
    en: "Track your deals, clients, mandates and pipelines right from your dashboards —",
    fr: "Suivi de vos deals, clients, mandats et pipelines directement sur vos tableaux de bord —",
  },
  highlight: { en: "GOODBYE CHAOS!", fr: "GOODBYE CHAOS!" },
  audience: { en: "All Users", fr: "All Users" },
};

const ROTATION_MS = 4200;
/** Per-row entrance stagger. Each side's card at index N animates at N * this. */
const ROW_STAGGER_MS = 400;
/** Base delay before the first cards animate in, letting the headline grow to full size first. */
const HEADLINE_LEAD_MS = 1600;

export function HeroMechanism() {
  const { t } = useLanguage();
  const lang = useLang();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setActive((prev) => (prev + 1) % PHRASES.length);
    }, ROTATION_MS);
    return () => window.clearInterval(id);
  }, []);

  const current = PHRASES[active];
  const buyerPhrases = PHRASES.filter((p) => p.side === "buyers");
  const partnerPhrases = PHRASES.filter((p) => p.side === "partners");
  // Both sides now have the same number of cards, so rows pair up cleanly.
  const rowCount = Math.max(buyerPhrases.length, partnerPhrases.length);

  return (
    <section className="relative overflow-hidden bg-brand-gradient">
      {/* Background photo */}
      <div className="absolute inset-0 overflow-hidden flex items-center justify-center">
        <img
          src={heroBackground}
          alt=""
          className="min-w-full min-h-full object-cover blur-[3px] opacity-25"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-br from-navy/85 via-navy-2/80 to-navy-dark/90" />

      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-accent/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-accent/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {/* Headline — title pops out from behind, subtitle follows, then cards animate in */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="animate-hero-pop motion-reduce:animate-none text-4xl md:text-6xl font-bold text-primary-foreground mb-5 leading-tight tracking-tight">
            {t("index.hero.title")}
          </h1>
          <p
            className="animate-hero-sub-fade motion-reduce:animate-none text-lg md:text-xl text-primary-foreground/85 max-w-3xl mx-auto leading-relaxed"
            style={{ animationDelay: "1100ms" }}
          >
            {t("index.hero.subtitle")}
          </p>
        </div>

        {/* Mechanism grid */}
        <div className="grid lg:grid-cols-[1fr_minmax(320px,460px)_1fr] gap-6 lg:gap-8 items-stretch">
          {/* Left — demand side (enters from the left) */}
          <SidePanel
            phrases={buyerPhrases}
            activeId={current.id}
            accent="gold"
            enterFrom="left"
          />

          {/* Center — the engine */}
          <CenterEngine current={current} />

          {/* Right — supply side (enters from the right) */}
          <SidePanel
            phrases={partnerPhrases}
            activeId={current.id}
            accent="blue"
            enterFrom="right"
          />
        </div>

        {/* All Users — full-width rectangular banner, slides in left → right */}
        <div
          className="mt-8 animate-card-in-left motion-reduce:animate-none"
          style={{ animationDelay: `${HEADLINE_LEAD_MS + rowCount * ROW_STAGGER_MS}ms` }}
        >
          <div className="rounded-2xl border border-accent/30 bg-white/5 backdrop-blur-sm px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
            <span className="shrink-0 self-start sm:self-center rounded-full bg-accent/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-accent">
              {ALL_USERS.audience[lang]}
            </span>
            <p className="text-base md:text-lg font-medium text-primary-foreground/90 leading-snug">
              {ALL_USERS.prefix[lang]}{" "}
              <span className="font-extrabold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-teal-200 to-emerald-400">
                {ALL_USERS.highlight[lang]}
              </span>
            </p>
          </div>
        </div>

        {/* Rotation progress dots */}
        <div className="mt-8 flex items-center justify-center gap-2">
          {PHRASES.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setActive(i)}
              aria-label={`Phrase ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === active
                  ? "w-8 bg-accent"
                  : "w-3 bg-primary-foreground/30 hover:bg-primary-foreground/50"
              }`}
            />
          ))}
        </div>

        {/* Role CTAs — keep all six */}
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <RoleLink to="/landing/sellers" variant="accent">
            {t("index.hero.im_seller")}
          </RoleLink>
          <RoleLink to="/landing/buyers" variant="secondary">
            {t("index.hero.im_buyer")}
          </RoleLink>
          <RoleLink to="/landing/consultants" variant="accent">
            {t("index.hero.im_consultant")}
          </RoleLink>
          <RoleLink to="/landing/brokers" variant="secondary">
            {t("index.hero.im_broker")}
          </RoleLink>
          <RoleLink to="/landing/lenders" variant="accent">
            {t("index.hero.im_lender")}
          </RoleLink>
          <RoleLink to="/register/partner" variant="secondary">
            {t("index.hero.im_internal_expert")}
          </RoleLink>
        </div>

        {/* Pipeline strip */}
        <div className="mt-14 border-t border-white/10 pt-6">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.2em] font-medium text-primary-foreground/45">
            {pipelineSteps(lang).map((step, i) => (
              <div key={step.label} className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      i % 2 === 0 ? "bg-accent" : "bg-primary-foreground/50"
                    }`}
                  />
                  <span>{step.label}</span>
                </div>
                {i < pipelineSteps(lang).length - 1 && (
                  <span className="text-primary-foreground/20">→</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function useLang(): "en" | "fr" {
  // Keep relying on the active language from context without re-rendering
  // the whole tree on language toggle for the side panels.
  const { language } = useLanguage();
  return language;
}

function CenterEngine({ current }: { current: Phrase }) {
  const lang = useLang();
  return (
    <div className="relative flex flex-col items-center justify-center py-6">
      {/* Rotating rings */}
      <div className="relative flex items-center justify-center">
        <div className="absolute h-64 w-64 rounded-full border border-dashed border-accent/30 animate-[spin_60s_linear_infinite] motion-reduce:animate-none" />
        <div className="absolute h-80 w-80 rounded-full border border-white/10 animate-[spin_45s_linear_infinite_reverse] motion-reduce:animate-none" />

        {/* Core badge */}
        <div className="relative z-10 h-44 w-44 rounded-full bg-gradient-to-br from-accent to-[#EAD67C] flex flex-col items-center justify-center text-center px-6 shadow-[0_0_60px_-10px_rgba(206,171,101,0.5)]">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-navy/70">
            The Engine
          </span>
          <span className="mt-1 text-lg font-bold leading-tight text-navy">
            {lang === "fr" ? "Le matching" : "The matching"}
          </span>
        </div>

        {/* Side connectors */}
        <div className="hidden lg:block absolute -left-12 top-1/2 h-px w-12 bg-gradient-to-r from-transparent to-accent/50" />
        <div className="hidden lg:block absolute -right-12 top-1/2 h-px w-12 bg-gradient-to-l from-transparent to-accent/50" />
      </div>

      {/* Active rotating phrase */}
      <div className="mt-6 w-full">
        <div
          key={current.id}
          className="rounded-2xl border border-accent/30 bg-white/5 backdrop-blur-sm px-6 py-5 text-center animate-[hero-fade_0.6s_ease-out]"
        >
          <span className="inline-block rounded-full bg-accent/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-accent">
            {current.audience[lang]}
          </span>
          <p className="mt-3 text-xl md:text-2xl font-semibold text-primary-foreground leading-snug">
            {current.text[lang]}
          </p>
        </div>
      </div>
    </div>
  );
}

function SidePanel({
  phrases,
  activeId,
  accent,
  enterFrom,
}: {
  phrases: Phrase[];
  activeId: number;
  accent: "gold" | "blue";
  enterFrom: "left" | "right";
}) {
  const lang = useLang();
  const dot = accent === "gold" ? "bg-accent" : "bg-sky-400/80";
  const ringActive =
    accent === "gold"
      ? "border-accent/50 bg-accent/10"
      : "border-sky-400/50 bg-sky-400/10";
  // Entrance is delegated to an outer wrapper so the inner button's active
  // scale transform never conflicts with the entrance transform.
  const enterClass =
    enterFrom === "left" ? "animate-card-in-left" : "animate-card-in-right";

  return (
    <div className="flex flex-col gap-3">
      {phrases.map((p, index) => {
        const isActive = p.id === activeId;
        return (
          <div
            key={p.id}
            className={`${enterClass} motion-reduce:animate-none`}
            style={{ animationDelay: `${HEADLINE_LEAD_MS + index * ROW_STAGGER_MS}ms` }}
          >
            <button
              onClick={() => {
                /* side cards are read-only previews; clicking does nothing
                   beyond being a passive visual reference. Intentionally inert. */
              }}
              tabIndex={-1}
              className={`w-full text-left rounded-xl border p-5 transition-all duration-500 ${
                isActive
                  ? `${ringActive} scale-[1.015]`
                  : "border-white/10 bg-white/5 hover:bg-white/10"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-primary-foreground/60">
                  {p.audience[lang]}
                </span>
              </div>
              <p className="text-base text-primary-foreground/90 leading-snug">
                {p.text[lang]}
              </p>
            </button>
          </div>
        );
      })}
    </div>
  );
}

function RoleLink({
  to,
  variant,
  children,
}: {
  to: string;
  variant: "accent" | "secondary";
  children: React.ReactNode;
}) {
  return (
    <Link to={to}>
      <Button size="lg" variant={variant} className="min-w-44">
        {children}
      </Button>
    </Link>
  );
}

function pipelineSteps(lang: "en" | "fr") {
  return lang === "fr"
    ? [
        { label: "LOI" },
        { label: "Dossier" },
        { label: "Matching" },
        { label: "Financement" },
        { label: "Closing" },
      ]
    : [
        { label: "LOI" },
        { label: "File" },
        { label: "Matching" },
        { label: "Funding" },
        { label: "Closing" },
      ];
}
